import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SEO } from '../seo/SEO';
import { useAuth } from '../auth';
import { useI18n } from '../i18n';
import { ALL_THEMES, THEME_LABEL_KEYS, useTheme } from '../theme/ThemeContext';
import { AppHeader } from '../components/AppHeader';
import { useSmartBack } from '../hooks/useSmartBack';
import { ActivityCalendar } from '../components/ActivityCalendar';
import { TierBadge } from '../components/TierBadge';
import { LevelCard } from '../components/LevelBadge';
import { PremiumBadge } from '../components/PremiumBadge';
import './ProfilePage.css';

const API = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface Stats {
  games_played: number;
  games_won: number;
  total_time: number;
  best_time: number | null;
  total_mistakes: number;
  win_rate: number;
  rating: number;
  xp?: number;
  level?: number;
  xp_into_level?: number;
  xp_for_next?: number;
  by_difficulty: Record<string, { played: number; won: number; best_time: number | null; avg_time: number | null }>;
}

interface MpPlayerResult {
  nickname: string;
  place: number;
  score: number;
  mistakes: number;
  elapsed: number;
  is_finished: boolean;
}

interface GameHistory {
  id: string;
  difficulty: number;
  mistakes: number;
  hints_used: number;
  elapsed_time: number;
  score: number;
  is_finished: boolean;
  is_multiplayer: boolean;
  room_name: string | null;
  mp_results: MpPlayerResult[] | null;
  created_at: string | null;
}

interface FavoriteGame extends GameHistory {
  favorited_at: string | null;
  puzzle: number[][];
  solution: number[][];
}

interface Achievement {
  id: string;
  icon: string;
  unlocked: boolean;
  unlocked_at: string | null;
}

interface StreakInfo {
  current_streak: number;
  longest_streak: number;
  last_play_date: string | null;
}

type Tab = 'stats' | 'personal_bests' | 'history' | 'favorites' | 'achievements' | 'season_rewards' | 'settings';

export function ProfilePage() {
  const { user, logout, updateProfile, changePassword, deleteAccount, token, loading, refreshAccessToken, sendVerificationEmail } = useAuth();
  const { darkMode, toggleDarkMode, theme, setTheme, isPremiumTheme } = useTheme();
  const navigate = useNavigate();
  const smartBack = useSmartBack('/');
  const [searchParams] = useSearchParams();
  const { t } = useI18n();
  const [nickname, setNickname] = useState(user?.nickname || '');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [stats, setStats] = useState<Stats | null>(null);
  const [history, setHistory] = useState<GameHistory[]>([]);
  const [favorites, setFavorites] = useState<FavoriteGame[]>([]);
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [seasonRewards, setSeasonRewards] = useState<Array<{
    id: number;
    season_number: number;
    rank: number;
    final_rating: number;
    tier: string;
    badge_code: string;
    awarded_at: string | null;
  }>>([]);
  const [streak, setStreak] = useState<StreakInfo | null>(null);
  const [activity, setActivity] = useState<Record<string, { solo: number; multiplayer: number; daily: number }>>({})
  const [personalBests, setPersonalBests] = useState<Record<string, { best_time: number | null; best_score: number | null; flawless_wins: number; games_played: number }>>({});
  const [statModes, setStatModes] = useState<Set<'solo' | 'multiplayer' | 'daily'>>(new Set(['solo', 'multiplayer', 'daily']));
  const [verifyEmailSending, setVerifyEmailSending] = useState(false);
  const [verifyEmailSent, setVerifyEmailSent] = useState(false);
  const [tab, setTab] = useState<Tab>(
    (searchParams.get('tab') as Tab) || 'stats'
  );

  useEffect(() => {
    if (!loading && !user) {
      navigate('/auth');
      return;
    }
  }, [user, loading, navigate]);

  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!token || fetchedRef.current) return;
    fetchedRef.current = true;

    const fetchWithRefresh = async () => {
      let t = token;

      const doFetch = async (url: string) => {
        const r = await fetch(url, { headers: { Authorization: `Bearer ${t}` } });
        if (r.status === 401) {
          const newToken = await refreshAccessToken();
          if (!newToken) return null;
          t = newToken;
          const r2 = await fetch(url, { headers: { Authorization: `Bearer ${t}` } });
          return r2.ok ? r2.json() : null;
        }
        return r.ok ? r.json() : null;
      };

      const [histData, favData, favIdsData, achData, streakData, userProfile, pbData, rewardsData] = await Promise.all([
        doFetch(`${API}/stats/history?limit=20`).catch(() => null),
        doFetch(`${API}/stats/favorites`).catch(() => null),
        doFetch(`${API}/stats/favorites/ids`).catch(() => null),
        doFetch(`${API}/stats/achievements`).catch(() => null),
        doFetch(`${API}/stats/streak`).catch(() => null),
        user?.id ? fetch(`${API}/stats/user/${user.id}`).then(r => r.ok ? r.json() : null).catch(() => null) : Promise.resolve(null),
        doFetch(`${API}/stats/personal-bests`).catch(() => null),
        doFetch(`${API}/stats/season/rewards`).catch(() => null),
      ]);

      if (histData) setHistory(histData.games ?? []);
      if (favData) setFavorites(favData.favorites ?? []);
      if (favIdsData) setFavoriteIds(new Set(favIdsData.ids ?? []));
      if (achData) setAchievements(achData.achievements ?? []);
      if (streakData) setStreak(streakData);
      if (userProfile?.activity) setActivity(userProfile.activity);
      if (pbData) setPersonalBests(pbData.personal_bests ?? {});
      if (rewardsData) setSeasonRewards(rewardsData.rewards ?? []);
    };

    fetchWithRefresh();
  }, [token]);

  // Stats mod filtresine göre yeniden çekilir
  useEffect(() => {
    if (!token) return;
    const modesStr = Array.from(statModes).join(',');
    fetch(`${API}/stats/me?modes=${modesStr}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setStats(data); })
      .catch(() => { /* ignore */ });
  }, [token, statModes]);

  const toggleFavorite = useCallback(async (gameId: string) => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/stats/favorites/${gameId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setFavoriteIds(prev => {
          const next = new Set(prev);
          if (data.favorited) next.add(gameId);
          else next.delete(gameId);
          return next;
        });
        if (data.favorited) {
          // puzzle/solution verisi için güncel listeyi çek
          const favRes = await fetch(`${API}/stats/favorites`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (favRes.ok) {
            const favData = await favRes.json();
            setFavorites(favData.favorites ?? []);
          }
        } else {
          setFavorites(prev => prev.filter(f => f.id !== gameId));
        }
      }
    } catch { /* ignore */ }
  }, [token]);

  // Oyun ayarları (localStorage)
  const [autoClean, setAutoClean] = useState(() => localStorage.getItem('sudoku_autoClean') !== 'false');
  const [highlight, setHighlight] = useState(() => localStorage.getItem('sudoku_highlight') !== 'false');
  const [cellHighlight, setCellHighlight] = useState(() => localStorage.getItem('sudoku_cellHighlight') !== 'false');

  const toggleGameSetting = (key: string, current: boolean, setter: (v: boolean) => void) => {
    const next = !current;
    localStorage.setItem(key, String(next));
    setter(next);
  };

  // Şifre değiştirme
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwMsg, setPwMsg] = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  // Hesap silme
  const [delPassword, setDelPassword] = useState('');
  const [delMsg, setDelMsg] = useState('');
  const [delLoading, setDelLoading] = useState(false);
  const [delConfirmStage, setDelConfirmStage] = useState(false);

  const handleChangePassword = async () => {
    if (pwNew !== pwConfirm) { setPwMsg(t.profile.pwMismatch); return; }
    if (pwNew.length < 6) { setPwMsg(t.profile.pwTooShort); return; }
    setPwSaving(true); setPwMsg('');
    try {
      await changePassword(pwCurrent, pwNew);
      setPwMsg(t.profile.pwUpdated);
      setPwCurrent(''); setPwNew(''); setPwConfirm('');
    } catch (err: any) {
      setPwMsg(err.message);
    } finally {
      setPwSaving(false);
    }
  };

  if (loading || !user) return <div className="profile-page" style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}><div className="spinner" /></div>;

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      await updateProfile({ nickname: nickname.trim() });
      setMessage(t.common.confirm);
    } catch (err: any) {
      setMessage(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleDeleteAccount = async () => {
    if (user?.auth_provider !== 'social' && !delPassword) {
      setDelMsg(t.profile.deleteAccountFailed);
      return;
    }
    setDelLoading(true); setDelMsg('');
    try {
      await deleteAccount(delPassword);
      navigate('/');
    } catch (err: any) {
      setDelMsg(err.message || t.profile.deleteAccountFailed);
      setDelLoading(false);
    }
  };

  const handleReplay = (fav: FavoriteGame) => {
    const puzzleStr = fav.puzzle.map(row => row.map(c => c || 0).join('')).join('');
    const solStr = fav.solution ? fav.solution.map(row => row.join('')).join('') : '';
    navigate(`/play?puzzle=${puzzleStr}&difficulty=${fav.difficulty}&solution=${solStr}`);
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div className="profile-page">
      <SEO path="/profile" />
      <AppHeader onBack={smartBack} backLabel={t.common.backToHome} title={t.auth.myAccount} />
      <div className="profile-top-actions">
        <p className="profile-email">
          {user.email}
          {user.is_premium && <PremiumBadge size="sm" className="profile-premium-badge" />}
        </p>
        <button className="friends-nav-btn" onClick={() => navigate('/friends')}>{t.profile.friendsBtn}</button>
      </div>

      {!(user as any).is_verified && (
        <div className="verify-email-banner">
          <span className="verify-email-banner__icon">📬</span>
          <span className="verify-email-banner__text">{t.profile.verifyEmailNotice}</span>
          <button
            className="verify-email-banner__btn"
            disabled={verifyEmailSending || verifyEmailSent}
            onClick={async () => {
              setVerifyEmailSending(true);
              try {
                await sendVerificationEmail();
                setVerifyEmailSent(true);
              } catch (e: any) {
                setMessage(e?.message || t.auth.errors.verificationEmailFailed);
                setTimeout(() => setMessage(''), 4000);
              } finally {
                setVerifyEmailSending(false);
              }
            }}
          >
            {verifyEmailSent ? t.profile.verifyEmailSent : verifyEmailSending ? '...' : t.profile.verifyEmailResend}
          </button>
        </div>
      )}

      {/* Tab Seçimi */}
      <div className="profile-tabs">
        <button
          className={`tab-btn ${tab === 'stats' ? 'active' : ''}`}
          onClick={() => setTab('stats')}
        >
          {t.profile.stats}
        </button>
        <button
          className={`tab-btn ${tab === 'personal_bests' ? 'active' : ''}`}
          onClick={() => setTab('personal_bests')}
        >
          {t.profile.personalBests}
        </button>
        <button
          className={`tab-btn ${tab === 'history' ? 'active' : ''}`}
          onClick={() => setTab('history')}
        >
          {t.profile.history}
        </button>
        <button
          className={`tab-btn ${tab === 'favorites' ? 'active' : ''}`}
          onClick={() => setTab('favorites')}
        >
          {t.profile.favorites}
        </button>
        <button
          className={`tab-btn ${tab === 'achievements' ? 'active' : ''}`}
          onClick={() => setTab('achievements')}
        >
          {t.profile.achievements}
        </button>
        <button
          className={`tab-btn ${tab === 'season_rewards' ? 'active' : ''}`}
          onClick={() => setTab('season_rewards')}
        >
          {t.profile.seasonRewards}
        </button>
        <button
          className={`tab-btn ${tab === 'settings' ? 'active' : ''}`}
          onClick={() => setTab('settings')}
        >
          {t.profile.settingsTab}
        </button>
      </div>

      {/* Kişisel Rekorlar */}
      {tab === 'personal_bests' && (
        <div className="pb-section">
          {[1, 2, 3, 4, 5].map(diff => {
            const pb = personalBests[String(diff)];
            const diffLabel = t.difficulties[diff];
            return (
              <div key={diff} className="pb-card">
                <div className="pb-card-header">
                  <span className="pb-diff-label">{diffLabel}</span>
                  {pb?.games_played ? (
                    <span className="pb-games-played">{pb.games_played} {t.profile.gamesPlayed}</span>
                  ) : null}
                </div>
                {(!pb || pb.games_played === 0) ? (
                  <p className="pb-no-games">{t.profile.pbNoGames}</p>
                ) : (
                  <div className="pb-stats-row">
                    <div className="pb-stat">
                      <span className="pb-stat-val">
                        {pb.best_time != null
                          ? `${Math.floor(pb.best_time / 60)}:${String(Math.round(pb.best_time % 60)).padStart(2, '0')}`
                          : '—'}
                      </span>
                      <span className="pb-stat-label">{t.profile.pbBestTime}</span>
                    </div>
                    <div className="pb-stat">
                      <span className="pb-stat-val">{pb.best_score ?? '—'}</span>
                      <span className="pb-stat-label">{t.profile.pbBestScore}</span>
                    </div>
                    <div className="pb-stat">
                      <span className="pb-stat-val">{pb.flawless_wins}</span>
                      <span className="pb-stat-label">{t.profile.pbFlawless}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* İstatistikler */}
      {tab === 'stats' && stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{stats.games_played}</div>
            <div className="stat-label">{t.profile.gamesPlayed}</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.games_won}</div>
            <div className="stat-label">{t.profile.gamesWon}</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">%{stats.win_rate}</div>
            <div className="stat-label">{t.profile.winRate}</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {stats.best_time ? formatTime(stats.best_time) : '—'}
            </div>
            <div className="stat-label">{t.profile.bestTime}</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.total_mistakes}</div>
            <div className="stat-label">{t.profile.totalMistakes}</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {stats.total_time ? formatTime(stats.total_time) : '—'}
            </div>
            <div className="stat-label">{t.profile.totalTime}</div>
          </div>
          {stats.rating > 0 && (
            <div className="stat-card stat-card--rating">
              <div className="stat-value">{stats.rating}</div>
              <div className="stat-label">{t.profile.rankedElo}</div>
              <div style={{ marginTop: 6 }}>
                <TierBadge rating={stats.rating} size="md" showProgress />
              </div>
            </div>
          )}
          {(stats.xp ?? 0) >= 0 && (
            <div className="stat-card stat-card--rating" style={{ gridColumn: 'span 2' }}>
              <div className="stat-label" style={{ marginBottom: 8 }}>{t.profile.level}</div>
              <LevelCard xp={stats.xp ?? 0} />
            </div>
          )}
        </div>
      )}

      {tab === 'stats' && (
        <ActivityCalendar activity={activity} modes={statModes} onModesChange={setStatModes} />
      )}

      {tab === 'stats' && stats && (() => {
        const diffEntries = [1, 2, 3, 4, 5]
          .map(d => ({ d, data: stats.by_difficulty[String(d)] }))
          .filter(({ data }) => data && data.played > 0);

        if (diffEntries.length === 0) return null;

        const bestDiff = diffEntries
          .filter(({ data }) => data.played >= 2)
          .sort((a, b) => (b.data.won / b.data.played) - (a.data.won / a.data.played))[0];
        const favDiff = diffEntries
          .sort((a, b) => b.data.played - a.data.played)[0];

        return (
          <div className="insights-section">
            <h3 className="insights-title">{t.profile.insightsTitle}</h3>
            <div className="diff-analysis-grid">
              {diffEntries.map(({ d, data }) => (
                <div key={d} className="diff-analysis-card">
                  <div className="diff-analysis-name">{t.difficulties[d]}</div>
                  <div className="diff-analysis-rows">
                    <div className="diff-analysis-row">
                      <span className="da-label">{t.profile.gamesLabel}</span>
                      <span className="da-value">{data.played}</span>
                    </div>
                    <div className="diff-analysis-row">
                      <span className="da-label">{t.profile.winRateLabel}</span>
                      <span className="da-value">
                        {data.played > 0 ? `%${Math.round(data.won / data.played * 100)}` : '—'}
                      </span>
                    </div>
                    <div className="diff-analysis-row">
                      <span className="da-label">{t.profile.avgTimeLabel}</span>
                      <span className="da-value">
                        {data.avg_time ? formatTime(data.avg_time) : '—'}
                      </span>
                    </div>
                    <div className="diff-analysis-row">
                      <span className="da-label">{t.profile.bestTimeLabel}</span>
                      <span className="da-value">
                        {data.best_time ? formatTime(data.best_time) : '—'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="insight-chips">
              {bestDiff && (
                <div className="insight-chip">
                  {t.profile.bestDiffLabel}: <strong>{t.difficulties[bestDiff.d]}</strong>
                  {' '}(%{Math.round(bestDiff.data.won / bestDiff.data.played * 100)} {t.profile.winRateLabel.toLowerCase()})
                </div>
              )}
              {favDiff && (
                <div className="insight-chip">
                  {t.profile.favDiffLabel}: <strong>{t.difficulties[favDiff.d]}</strong>
                  {' '}({favDiff.data.played} {t.profile.gamesLabel.toLowerCase()})
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {tab === 'stats' && !stats && (
        <p className="empty-state">{t.profile.noStats}</p>
      )}

      {/* Oyun Geçmişi */}
      {tab === 'history' && (
        <div className="history-list">
          <div className="history-toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            {!user?.is_premium && (
              <div className="history-limit-banner" style={{ flex: 1, padding: '8px 12px', background: 'linear-gradient(90deg, rgba(245,158,11,0.12), rgba(249,115,22,0.06))', border: '1px solid rgba(245,158,11,0.4)', borderRadius: 10, fontSize: 13 }}>
                🔒 {t.profile.historyLimitedFree}
              </div>
            )}
            <button
              className="export-btn"
              style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border, rgba(15,23,42,0.1))', background: user?.is_premium ? 'linear-gradient(90deg, #f59e0b, #f97316)' : 'var(--surface, #f8fafc)', color: user?.is_premium ? '#fff' : 'var(--text-muted, #64748b)', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}
              onClick={async () => {
                if (!user?.is_premium) { navigate('/premium'); return; }
                try {
                  const res = await fetch(`${API}/stats/export.csv`, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  if (!res.ok) throw new Error('export failed');
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `sudoku-history-${user.id}.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                } catch {
                  /* ignore */
                }
              }}
            >
              📥 {t.profile.exportCsv}{!user?.is_premium ? ' 🔒' : ''}
            </button>
          </div>
          {history.length === 0 ? (
            <p className="empty-state">{t.profile.noHistory}</p>
          ) : (
            history.map(game => (
              <div key={game.id} className="history-item-wrap">
                <div className="history-item">
                  <div className="history-diff">
                    {t.difficulties[game.difficulty]}
                    {game.is_multiplayer && <span className="history-mp-badge">MP</span>}
                  </div>
                  <div className="history-details">
                    <span>{formatTime(game.elapsed_time)}</span>
                    <span>{game.mistakes} {t.common.mistakes.toLowerCase()}</span>
                    <span>{game.hints_used} {t.common.hints.toLowerCase()}</span>
                    <span className="history-score">{t.profile.pointsValue.replace('{n}', String(game.score))}</span>
                    {!game.is_finished && <span className="history-failed">{t.profile.lost}</span>}
                  </div>
                  <div className="history-actions">
                    <button
                      className={`fav-btn ${favoriteIds.has(game.id) ? 'fav-active' : ''}`}
                      onClick={() => toggleFavorite(game.id)}
                      title={favoriteIds.has(game.id) ? t.profile.removeFavorite : t.profile.addFavorite}
                    >
                      {favoriteIds.has(game.id) ? '★' : '☆'}
                    </button>
                    <span className="history-date">
                      {game.room_name && <span className="history-room-name">{game.room_name}</span>}
                      {game.created_at
                        ? new Date(game.created_at).toLocaleDateString()
                        : ''}
                    </span>
                  </div>
                </div>
                {game.mp_results && game.mp_results.length > 0 && (
                  <div className="history-mp-detail">
                    {game.mp_results.map((p, i) => (
                      <div key={i} className="mp-result-row">
                        <span className="mp-result-place">#{p.place}</span>
                        <span className="mp-result-name">{p.nickname}</span>
                        <span className="mp-result-score">{t.profile.pointsValue.replace('{n}', String(p.score))}</span>
                        <span className="mp-result-stats">{t.profile.mistakesTime.replace('{mistakes}', String(p.mistakes)).replace('{time}', formatTime(p.elapsed))}</span>
                        {!p.is_finished && <span className="history-failed">{t.profile.lost}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Favoriler */}
      {tab === 'favorites' && (
        <div className="favorites-list">
          {favorites.length === 0 ? (
            <p className="empty-state">{t.profile.noFavorites}</p>
          ) : (
            favorites.map(fav => (
              <div key={fav.id} className="history-item-wrap">
                <div className="history-item">
                  <div className="history-diff">
                    {t.difficulties[fav.difficulty]}
                  </div>
                  <div className="history-details">
                    <span>{formatTime(fav.elapsed_time)}</span>
                    <span>{fav.mistakes} {t.common.mistakes.toLowerCase()}</span>
                    <span className="history-score">{fav.score} puan</span>
                  </div>
                  <div className="history-actions">
                    <button className="replay-btn" onClick={() => handleReplay(fav)}>
                      🔄 {t.profile.replay}
                    </button>
                    <button
                      className="fav-btn fav-active"
                      onClick={() => toggleFavorite(fav.id)}
                      title={t.profile.removeFavorite}
                    >
                      ★
                    </button>
                    <span className="history-date">
                      {fav.created_at ? new Date(fav.created_at).toLocaleDateString() : ''}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Başarımlar & Streak */}
      {tab === 'achievements' && (
        <div className="achievements-section">
          {streak && (
            <div className="streak-card">
              <h3>{t.profile.streakTitle}</h3>
              <div className="streak-grid">
                <div className="streak-item">
                  <div className="streak-value">{streak.current_streak}</div>
                  <div className="streak-label">{t.profile.currentStreak}</div>
                </div>
                <div className="streak-item">
                  <div className="streak-value">{streak.longest_streak}</div>
                  <div className="streak-label">{t.profile.longestStreak}</div>
                </div>
              </div>
            </div>
          )}

          <div className="achievements-grid">
            {achievements.map(ach => {
              const nameKey = `ach_${ach.id}` as keyof typeof t.profile;
              const descKey = `ach_${ach.id}_desc` as keyof typeof t.profile;
              return (
                <div
                  key={ach.id}
                  className={`achievement-card ${ach.unlocked ? 'unlocked' : 'locked'}`}
                >
                  <div className="ach-icon">{ach.icon}</div>
                  <div className="ach-info">
                    <div className="ach-name">{(t.profile as any)[nameKey] || ach.id}</div>
                    <div className="ach-desc">{(t.profile as any)[descKey] || ''}</div>
                    {ach.unlocked && ach.unlocked_at && (
                      <div className="ach-date">
                        {t.profile.unlocked}: {new Date(ach.unlocked_at).toLocaleDateString()}
                      </div>
                    )}
                    {!ach.unlocked && (
                      <div className="ach-locked-label">{t.profile.locked}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Sezon Rozetleri */}
      {tab === 'season_rewards' && (
        <div className="season-rewards-section">
          <h3 className="season-rewards-title">{t.profile.seasonRewardsTitle}</h3>
          {seasonRewards.length === 0 ? (
            <p className="season-rewards-empty">{t.profile.seasonRewardsEmpty}</p>
          ) : (
            <div className="season-rewards-grid">
              {seasonRewards.map(rw => {
                const tierLabel =
                  rw.tier === 'top10' ? t.profile.seasonRewardTop10
                  : rw.tier === 'top100' ? t.profile.seasonRewardTop100
                  : t.profile.seasonRewardTop1000;
                const ringStyle: React.CSSProperties = {
                  background:
                    rw.tier === 'top10' ? 'linear-gradient(135deg, #60a5fa, #818cf8)'
                    : rw.tier === 'top100' ? 'linear-gradient(135deg, #fbbf24, #f59e0b)'
                    : 'linear-gradient(135deg, #cbd5e1, #94a3b8)',
                };
                const seasonLabel = t.profile.seasonRewardSeason.replace('{n}', String(rw.season_number));
                return (
                  <div key={rw.id} className={`season-reward-card season-reward-${rw.tier}`}>
                    <div className="season-reward-ring" style={ringStyle}>
                      <span className="season-reward-rank">#{rw.rank}</span>
                    </div>
                    <div className="season-reward-info">
                      <div className="season-reward-season">{seasonLabel}</div>
                      <div className="season-reward-tier">{tierLabel}</div>
                      <div className="season-reward-rating">{rw.final_rating} ELO</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Ayarlar */}
      {tab === 'settings' && (
        <div className="settings-tab">
          {/* Oyun Ayarları */}
          <div className="settings-section">
            <h3 className="settings-section-title">{t.profile.gameSettingsTitle}</h3>
            <div className="settings-row" onClick={() => toggleGameSetting('sudoku_autoClean', autoClean, setAutoClean)}>
              <div className="settings-row-info">
                <span className="settings-row-label">{t.profile.autoCleanLabel}</span>
                <span className="settings-row-desc">{t.profile.autoCleanDesc}</span>
              </div>
              <div className={`settings-toggle ${autoClean ? 'on' : ''}`}><div className="settings-toggle-thumb" /></div>
            </div>
            <div className="settings-row" onClick={() => toggleGameSetting('sudoku_highlight', highlight, setHighlight)}>
              <div className="settings-row-info">
                <span className="settings-row-label">{t.profile.highlightLabel}</span>
                <span className="settings-row-desc">{t.profile.highlightDesc}</span>
              </div>
              <div className={`settings-toggle ${highlight ? 'on' : ''}`}><div className="settings-toggle-thumb" /></div>
            </div>
            <div className="settings-row" onClick={() => toggleGameSetting('sudoku_cellHighlight', cellHighlight, setCellHighlight)}>
              <div className="settings-row-info">
                <span className="settings-row-label">{t.profile.cellHighlightLabel}</span>
                <span className="settings-row-desc">{t.profile.cellHighlightDesc}</span>
              </div>
              <div className={`settings-toggle ${cellHighlight ? 'on' : ''}`}><div className="settings-toggle-thumb" /></div>
            </div>
            <div className="settings-row" onClick={toggleDarkMode}>
              <div className="settings-row-info">
                <span className="settings-row-label">{t.common.darkMode}</span>
                <span className="settings-row-desc">{t.theme.pickerDesc}</span>
              </div>
              <div className={`settings-toggle ${darkMode ? 'on' : ''}`}><div className="settings-toggle-thumb" /></div>
            </div>

            {/* Tema Seçici */}
            <div className="settings-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 10, cursor: 'default' }}>
              <div className="settings-row-info">
                <span className="settings-row-label">{t.theme.pickerTitle}</span>
                <span className="settings-row-desc">{t.theme.pickerDesc}</span>
              </div>
              <div className="theme-choice-grid">
                {ALL_THEMES.map(name => {
                  const locked = isPremiumTheme(name) && !user?.is_premium;
                  const active = theme === name;
                  const label = (t.theme as Record<string, string>)[THEME_LABEL_KEYS[name]];
                  return (
                    <button
                      key={name}
                      onClick={() => {
                        if (locked) { navigate('/premium'); return; }
                        setTheme(name);
                      }}
                      title={locked ? t.theme.premiumLocked : label}
                      className={`theme-choice ${active ? 'active' : ''} ${locked ? 'locked' : ''}`}
                    >
                      <div className={`theme-choice-swatch theme-menu-swatch--${name}`} />
                      <div className="theme-choice-name">
                        {label}
                        {locked && <span className="theme-choice-lock">🔒</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Hesap Ayarları */}
          <div className="settings-section">
            <h3 className="settings-section-title">{t.profile.accountSettingsTitle}</h3>

            <div className="settings-field-group">
              <label className="settings-label">{t.profile.nicknameLabel}</label>
              <div className="settings-input-row">
                <input
                  type="text"
                  className="settings-input"
                  value={nickname}
                  onChange={e => setNickname(e.target.value)}
                  maxLength={50}
                />
                <button className="settings-save-btn" onClick={handleSave} disabled={saving}>
                  {saving ? '...' : t.profile.saveBtn}
                </button>
              </div>
              {message && <p className="settings-msg">{message}</p>}
            </div>

            <div className="settings-field-group">
              <label className="settings-label">{t.profile.changePasswordTitle}</label>
              <input
                type="password"
                className="settings-input"
                placeholder={t.profile.pwCurrent}
                value={pwCurrent}
                onChange={e => setPwCurrent(e.target.value)}
              />
              <input
                type="password"
                className="settings-input"
                placeholder={t.profile.pwNew}
                value={pwNew}
                onChange={e => setPwNew(e.target.value)}
              />
              <input
                type="password"
                className="settings-input"
                placeholder={t.profile.pwConfirm}
                value={pwConfirm}
                onChange={e => setPwConfirm(e.target.value)}
              />
              <button className="settings-save-btn" onClick={handleChangePassword} disabled={pwSaving}>
                {pwSaving ? '...' : t.profile.updatePasswordBtn}
              </button>
              {pwMsg && <p className="settings-msg">{pwMsg}</p>}
            </div>
          </div>

          <div className="settings-section settings-section--danger" style={{ marginTop: 16 }}>
            <h3 className="settings-section-title">{t.profile.deleteAccountTitle}</h3>
            <div className="settings-danger-body">
              <p className="settings-danger-desc">
                {t.profile.deleteAccountWarning}
              </p>
              {!delConfirmStage ? (
                <button
                  className="settings-danger-btn"
                  onClick={() => setDelConfirmStage(true)}
                >
                  {t.profile.deleteAccountTitle}
                </button>
              ) : (
                <>
                  {user?.auth_provider !== 'social' && (
                    <input
                      type="password"
                      className="settings-input"
                      placeholder={t.profile.deleteAccountPasswordPlaceholder}
                      value={delPassword}
                      onChange={e => setDelPassword(e.target.value)}
                      style={{ width: '100%' }}
                    />
                  )}
                  <div className="settings-danger-actions">
                    <button
                      className="settings-danger-cancel"
                      onClick={() => { setDelConfirmStage(false); setDelPassword(''); setDelMsg(''); }}
                    >
                      {t.common.cancel}
                    </button>
                    <button
                      className="settings-danger-confirm"
                      onClick={handleDeleteAccount}
                      disabled={delLoading}
                    >
                      {delLoading ? '...' : t.profile.deleteAccountConfirm}
                    </button>
                  </div>
                </>
              )}
              {delMsg && <p className="settings-danger-msg">{delMsg}</p>}
            </div>
          </div>

          <button className="logout-btn" onClick={handleLogout}>
            {t.auth.logout}
          </button>
        </div>
      )}

    </div>
  );
}
